import type { FleetPortalInvoice } from "@station/contracts";
import { Alert, DataTable, type DataTableColumn, EmptyState, MoneyText, StatusBadge } from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { FileText } from "lucide-react";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { moneyExport } from "../../features/market/market-values.js";
import { usePortal } from "../../features/portal/portal-context.js";
import { usePortalInvoices } from "../../features/portal/queries.js";
import { PortalHeader } from "../../features/portal/shared.js";
import { compact } from "../../features/market/queries.js";
import { api } from "../../lib/api.js";
import { useCursorPages } from "../../lib/cursor-pages.js";
import { errorMessage } from "../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../lib/excel.js";
import { formatDate } from "../../lib/format.js";

export const Route = createFileRoute("/portal/faturalar")({ component: PortalInvoicesRoute });

/**
 * Filonun satış faturaları (yalnızca OKUNUR): kesilmiş ve iptal edilmiş faturalar; dönem faturası "fişli satış faturası"
 * olarak görünür. Fatura belgesinin (PDF / UBL) indirilmesi portalda yoktur (sözleşmede uç yok).
 */
function PortalInvoicesRoute(): ReactElement {
  const { t } = useTranslation();
  const portal = usePortal();
  const pages = useCursorPages(portal.counterpartyId);
  const list = usePortalInvoices(portal.counterpartyId, pages.cursor);

  const columns = useMemo<DataTableColumn<FleetPortalInvoice>[]>(
    () => [
      {
        id: "invoiceNo",
        header: t("portal.fields.invoiceNo"),
        hideable: false,
        exportValue: (row) => row.invoiceNo,
        cell: (row) => <span className="font-mono">{row.invoiceNo ?? "—"}</span>,
      },
      {
        id: "company",
        header: t("portal.fields.company"),
        exportValue: (row) => row.companyName,
        cell: (row) => row.companyName,
      },
      {
        id: "basis",
        header: t("portal.fields.basis"),
        exportValue: (row) => t(`portal.invoices.basis.${row.basis}`),
        cell: (row) => t(`portal.invoices.basis.${row.basis}`),
      },
      {
        id: "issueDate",
        header: t("portal.fields.issueDate"),
        exportValue: (row) => row.issueDate,
        cell: (row) => <span className="tabular">{formatDate(row.issueDate)}</span>,
      },
      {
        id: "dueDate",
        header: t("portal.fields.dueDate"),
        exportValue: (row) => row.dueDate,
        cell: (row) => <span className="tabular">{formatDate(row.dueDate)}</span>,
      },
      {
        id: "payable",
        header: t("portal.fields.payable"),
        align: "right",
        exportValue: (row) => moneyExport(row.payableAmount),
        cell: (row) => <MoneyText value={row.payableAmount} />,
      },
      {
        id: "status",
        header: t("portal.fields.status"),
        exportValue: (row) => t(`portal.invoices.status.${row.status}`),
        cell: (row) => (
          <StatusBadge tone={row.status === "ISSUED" ? "success" : "neutral"}>
            {t(`portal.invoices.status.${row.status}`)}
          </StatusBadge>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      <PortalHeader title={t("portal.invoices.title")} description={t("portal.invoices.description")} />
      <DataTable
        caption={t("portal.invoices.title")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<FileText />}
            title={t("portal.invoices.emptyTitle")}
            description={t("portal.invoices.emptyDescription")}
          />
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<FleetPortalInvoice>(
            async (cursor) =>
              (
                await api.GET("/fleet-portal/invoices", {
                  params: {
                    query: compact({ counterpartyId: portal.counterpartyId, cursor, limit: 200 }) as never,
                  },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: `filo-faturalar-${portal.account.counterpartyCode}`,
            sheetName: t("portal.invoices.title"),
            columns: visible,
            rows,
          });
        }}
      />
    </>
  );
}
